import { useNavigation } from "@react-navigation/native";
import { nanoid } from "@reduxjs/toolkit";
import { useState } from "react";
import {
  StyleSheet,
  Text,
  TouchableWithoutFeedback,
  View,
  ViewStyle,
} from "react-native";
import { useSelector } from "react-redux";
import { RootState } from "../../redux";
import { Colors, Spacing, Typography } from "../../styles";
import { InsightID } from "../../types/data";

const VisibilityBar = ({ visibility }: { visibility: number }) => (
  <View
    style={[
      styles.visibilityBar,
      {
        width: `${visibility}%`,
        backgroundColor: `rgba(255,255,255,${visibility / 100})`,
      },
    ]}
  />
);

type Visualisation =
  | { type: "bar"; data: number[] }
  | { type: "percentage"; data: number };

export interface CardProps {
  id: InsightID;
  style?: ViewStyle;
  interactive?: boolean;
}

const Visualisation = ({
  visualisation,
  style,
}: {
  visualisation: Visualisation | undefined;
  style?: ViewStyle;
}) => {
  if (!visualisation) return <></>;

  if (visualisation.type === "bar") {
    const data = visualisation.data;
    const maxData = Math.max(...data);
    const barSpacing = 3;
    return (
      <View
        style={[
          {
            height: 200,
            width: "70%",
            flexDirection: "row",
            alignItems: "flex-end",
            justifyContent: "space-between",
          },
          style,
        ]}
      >
        {data.map((d, index) => (
          <View
            key={nanoid()}
            style={{
              width: `${(1 / data.length) * 100 - barSpacing}%`,
              height: `${(d / maxData) * 100}%`,
              borderWidth: 1,
              borderColor:
                index === data.length - 1 ? Colors.black : Colors.white,
              backgroundColor:
                index === data.length - 1 ? Colors.teal300 : "transparent",
            }}
          />
        ))}
      </View>
    );
  } else if (visualisation.type === "percentage") {
    const num = Math.round(visualisation.data / 10);

    return (
      <View
        style={[
          {
            flexDirection: "row",
            flexWrap: "wrap",
            justifyContent: "space-between",
          },
          style,
        ]}
      >
        {Array.from(Array(10).keys()).map((i) => (
          <View
            key={nanoid()}
            style={{
              width: "17.5%",
              paddingBottom: "17.5%",
              marginBottom: 10,
              borderRadius: 100,
              borderColor: i < num ? Colors.black : Colors.white,
              backgroundColor: i < num ? Colors.teal300 : "transparent",
              borderWidth: 1,
            }}
          />
        ))}
      </View>
    );
  } else return <></>;
};

const Card = ({ id, style, interactive }: CardProps) => {
  const navigation = useNavigation();

  //#region Card styles
  const [contentHeight, setContentHeight] = useState<number | undefined>();
  const contentHeightBasedStyles = [
    {
      minHeight: 120,
      // flex: 1,
      // paddingVertical: 0,
      justifyContent: "center",
    },
    {
      minHeight: 200,
    },
    { minHeight: 300 },
  ];
  let heightBasedStyle = contentHeightBasedStyles.find(
    (s) => s.minHeight > contentHeight
  );
  //#endregion

  //#region Get data
  const insight = useSelector((state: RootState) =>
    state.main.insights.find((i) => i.id === id)
  );

  const entity = useSelector((state: RootState) => state.main.entity);

  if (!insight) return <></>;
  const visibility = insight.visibilities.find((v) => v.entity === entity);
  if (!visibility) return <></>;

  const visibilityData = visibility.data;
  let [visibilityCurrent] = visibilityData.slice(-1);
  //#endregion

  return (
    <TouchableWithoutFeedback
      onPress={() => {
        if (interactive !== false)
          navigation.push("Insight", {
            id,
          });
      }}
    >
      <View style={[styles.container, style]}>
        <View
          style={[styles.inner, heightBasedStyle]}
          onLayout={(e) => {
            if (!contentHeight) setContentHeight(e.nativeEvent.layout.height);
          }}
        >
          <Text style={Typography.cardTitle}>{insight.title}</Text>
          <Visualisation
            visualisation={insight.visualisation}
            style={{ marginTop: 20 }}
          />
        </View>
        <VisibilityBar visibility={visibilityCurrent} />
      </View>
    </TouchableWithoutFeedback>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.plum700,
    borderRadius: Spacing.cardRadius,
    overflow: "hidden",
    justifyContent: "space-between",
  },
  inner: {
    padding: Spacing.card,
    paddingVertical: Spacing.card * 1.5,
  },
  visibilityBar: {
    width: "100%",
    height: 5,
  },
});

export default Card;
